import Link from 'next/link'

import Layout from '@components/layout/Main'
import FAQ from '@components/shared/FAQ'
import SvgIcon from '@components/shared/svg-icons/SvgIcon'
import StaticCrumbs, { breadcrumbLd } from '@components/static-pages/StaticCrumbs'
import Business from '@constants/Business'
import { COLISION_DOC_PAGE, SALE_BUY_DOC_PAGE, SALE_DOC_PAGE } from '@constants/routes'
import Seo from 'src/seo/components/Seo'
import jsonLd from 'src/seo/jsonld/functions/_jsonLd'
import mix from '@utils/styles/mix'
import DocCss from './docCss'
import DocumentTool from './DocumentTool'
import * as umowa from './content/umowa'
import * as oswiadczenie from './content/oswiadczenie'
import * as zgloszenie from './content/zgloszenie'

import faqStyles from '@components/firmbase/v2/FirmBaseFaq.module.css'
import page from '@components/static-pages/StaticPage.module.css'
import styles from './Documents.module.css'

const HOST = process.env.NEXT_PUBLIC_HOST

// h1 — gdy reguła SEO strony nie ma H1; name — nazwa WebPage w danych strukturalnych (jak dotąd, bez literówki)
const DOCS = {
    umowa: {
        url: SALE_DOC_PAGE, crumb: 'Umowa kupna-sprzedaży', card: 'Wypełnij i wydrukuj', content: umowa,
        h1: 'Umowa sprzedaży samochodu', name: 'Umowa sprzedaży',
        lead: 'Wypełnij formularz, sprawdź podgląd i wydrukuj umowę albo zapisz ją jako PDF. Możesz też wydrukować pusty wzór i wypełnić go ręcznie.',
    },
    oswiadczenie: {
        url: COLISION_DOC_PAGE, crumb: 'Oświadczenie sprawcy kolizji', card: 'Wypełnij albo wydrukuj pusty wzór', content: oswiadczenie,
        h1: 'Oświadczenie sprawcy kolizji', name: 'Oświadczenie sprawcy kolizji',
        lead: 'Wypełnij oświadczenie i wydrukuj je albo zapisz jako PDF. Pusty wzór warto wydrukować zawczasu i wozić w schowku — na miejscu zdarzenia wypełnisz go ręcznie.',
    },
    zgloszenie: {
        url: SALE_BUY_DOC_PAGE, crumb: 'Zgłoszenie zbycia lub nabycia pojazdu', card: 'Wzór zawiadomienia do urzędu', content: zgloszenie,
        h1: 'Zgłoszenie sprzedaży/kupna samochodu', name: 'Zgłoszenie sprzedaży/kupna samochodu',
        lead: 'Wypełnij zawiadomienie o zbyciu lub nabyciu pojazdu i wydrukuj je albo zapisz jako PDF. Możesz też wydrukować pusty wzór i wypełnić go ręcznie.',
    },
}

function ld (doc, crumbs) {
    return {
        '@context': 'https://schema.org',
        '@graph': [
            breadcrumbLd(crumbs),
            {
                '@type': 'WebPage',
                name: doc.name,
                url: HOST + doc.url,
                description: 'Generator dokumentów. Wygeneruj dokument gotowy do druku.',
                publisher: {
                    '@type': 'Organization',
                    name: 'Samochody.pl',
                    logo: { '@type': 'ImageObject', url: `${Business.host}/assets/icons/large-samochody-fb.png` },
                },
            },
        ],
    }
}

export default function DocumentPage ({ kind, seo }) {
    const doc = DOCS[kind]
    const crumbs = [{ name: 'Strona główna', url: '/' }, { name: doc.crumb, url: doc.url }]
    return (
        <Layout nofooterdiv>
            <Seo seo={seo} />
            {jsonLd(ld(doc, crumbs))}
            <DocCss />
            <Layout.NavSpace />
            <div className={page.main}>
                <div className={page.container}>
                    <StaticCrumbs items={crumbs} />
                    <header className={styles.head}>
                        <h1>{seo?.h1 || doc.h1}</h1>
                        <p className={styles.lead}>{doc.lead}</p>
                        <div className={styles.privacy}>
                            <SvgIcon name="lock" />
                            <div>
                                <p><strong>Dane zostają na Twoim urządzeniu.</strong></p>
                                <p>Dokument powstaje w przeglądarce: nie wysyłamy wpisanych danych na serwer i nigdzie ich nie zapisujemy. Po zamknięciu lub odświeżeniu strony formularz będzie pusty.</p>
                            </div>
                        </div>
                    </header>

                    <DocumentTool key={kind} kind={kind} />

                    <section aria-labelledby="doc-more" className={page.sec}>
                        <div className={page.secHead}><h2 id="doc-more">Inne dokumenty</h2></div>
                        <ul className={styles.cards}>
                            { Object.entries(DOCS).map(([k, d]) => (
                                <li key={k}>
                                    <Link aria-current={k === kind ? 'page' : undefined} className={styles.card} href={d.url} prefetch={false}>
                                        <SvgIcon name="doc" />
                                        <span>{d.crumb}<small>{d.card}</small></span>
                                    </Link>
                                </li>
                            )) }
                        </ul>
                    </section>

                    <section aria-label="Poradnik" className={page.sec}>
                        <article className={styles.article} dangerouslySetInnerHTML={{ __html: doc.content.ARTICLE }} />
                    </section>

                    <section aria-labelledby="doc-faq" className={mix([page.sec, styles.faq])}>
                        <div className={page.secHead}><h2 id="doc-faq">{doc.content.FAQ_TITLE}</h2></div>
                        <FAQ page={`doc-${kind}`} qa={doc.content.FAQ} styles={faqStyles} title={null} />
                    </section>
                </div>
            </div>
        </Layout>
    )
}
